import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EWidget, IMemoryStoredWidgetAttachmentFile } from 'scw-digital-boards-widgets';
import { API_CACHE_MODE_HEADER, EApiCacheMode } from '../../core/interceptors/api-interceptor.interceptor';
import {
  IDashboardWithWidgets,
  IWidget,
  TWidgetSavePayload,
} from '../../pages/dashboard-content/dashboard-content.interface';
import { IEditDashboard } from '../../pages/settings/dashboards/dashboards.interface';
import {
  IWidgetsHavingAttachments,
  MemoryStoredWidgetAttachmentFile,
} from '../../shared/components/image-upload/image-upload.interface';
import { HttpUtilitiesService, IBaseResponse, IGetManyResponse } from '../../utilities/http-utilities.service';

@Injectable({ providedIn: 'root' })
export class DashboardContentService {
  public readonly dashboardContentLoadParams = this.httpUtilities.insertGenericCrudRequestParameters({
    fields: [
      'name',
      'refreshInterval',
      'defaultFilterCard',
      'isInUseAsASubDashboard',
      'isWidgetFilterButtonHidden',
      'layoutModeId',
      'parentId',
    ],
    join: ['widgets||configuration,subtitle,title,type,typeBasedConfiguration,filters,subDashboardId', 'workspace'],
  });

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {}

  public getDashboardContent(
    dashboardId: number,
    options: Parameters<HttpClient['get']>[1] = {},
  ): Observable<IBaseResponse<IDashboardWithWidgets>> {
    return this.http.get<IBaseResponse<IDashboardWithWidgets>>(`dashboards/${dashboardId}`, options);
  }

  public reassignWidgets(
    dashboardId: number,
    payload: TWidgetSavePayload[],
  ): Observable<IBaseResponse<IDashboardWithWidgets>> {
    return this.http.patch<IBaseResponse<IDashboardWithWidgets>>(`dashboards/${dashboardId}/reassign-widgets`, {
      payload,
    });
  }

  public createAttachmentThenReassign(
    dashboardId: number,
    dashboardWidgets: TWidgetSavePayload[],
  ): Observable<IBaseResponse<IDashboardWithWidgets>> {
    const widgetsHavingAttachments: IWidgetsHavingAttachments[] =
      this.extractWidgetsHavingAttachments(dashboardWidgets);

    if (widgetsHavingAttachments.length === 0) {
      return this.reassignWidgets(dashboardId, dashboardWidgets);
    }

    const createdWidgetIds: Observable<number[]>[] = widgetsHavingAttachments.map(({ widget }) =>
      this.getAttachmentIds(dashboardId, widget),
    );

    return forkJoin(createdWidgetIds).pipe(
      switchMap((synchronizedIdLists: number[][]) => {
        const updatedWidgets: TWidgetSavePayload[] = this.mergeAttachmentIdsIntoWidgets(
          dashboardWidgets,
          widgetsHavingAttachments,
          synchronizedIdLists,
        );

        return this.reassignWidgets(dashboardId, updatedWidgets);
      }),
    );
  }

  private extractWidgetsHavingAttachments(widgets: TWidgetSavePayload[]): IWidgetsHavingAttachments[] {
    return widgets
      .map((widget, index) => ({ index, widget }))
      .filter(
        (item): item is { widget: TWidgetSavePayload<EWidget.imageViewer>; index: number } =>
          'imageUpload' in item.widget.typeBasedConfiguration &&
          Array.isArray(item.widget.typeBasedConfiguration.imageUpload) &&
          item.widget.typeBasedConfiguration.imageUpload.length > 0,
      );
  }

  private getAttachmentIds(dashboardId: number, widget: TWidgetSavePayload<EWidget.imageViewer>): Observable<number[]> {
    const imageUploadValue: IMemoryStoredWidgetAttachmentFile[] | number[] = widget.typeBasedConfiguration.imageUpload;

    if (this.isNumberArray(imageUploadValue)) {
      return of(imageUploadValue);
    }

    const attachmentList = imageUploadValue as MemoryStoredWidgetAttachmentFile[];
    const existingAttachmentIds: number[] = attachmentList.filter((file) => file?.id !== null).map((file) => file.id!);
    const newFilesToUpload: MemoryStoredWidgetAttachmentFile[] = attachmentList.filter((file) => !file?.id);

    if (newFilesToUpload.length === 0) {
      return of(existingAttachmentIds);
    }

    return this.createWidgetAttachment(dashboardId, newFilesToUpload).pipe(
      map((response: IGetManyResponse<number>): number[] => {
        const newIds: number[] = Array.isArray(response.data) ? response.data : [response.data];

        return existingAttachmentIds.concat(newIds);
      }),
    );
  }

  private isNumberArray(value: any): value is number[] {
    return Array.isArray(value) && value.length > 0 && typeof value[0] === 'number';
  }

  private mergeAttachmentIdsIntoWidgets(
    originalWidgets: TWidgetSavePayload[],
    processedWidgetInfo: { index: number; widget: TWidgetSavePayload }[],
    resolvedIdLists: number[][],
  ): TWidgetSavePayload[] {
    const updatedWidgets: TWidgetSavePayload[] = [...originalWidgets];

    processedWidgetInfo.forEach((info, listIndex) => {
      const { widget, index } = info;
      updatedWidgets[index] = {
        ...widget,
        typeBasedConfiguration: {
          ...widget.typeBasedConfiguration,
          imageUpload: resolvedIdLists[listIndex],
        },
      };
    });

    return updatedWidgets;
  }

  public createWidgetAttachment(
    dashboardId: number,
    attachments: MemoryStoredWidgetAttachmentFile[],
  ): Observable<IGetManyResponse<number>> {
    const formData = new FormData();

    formData.append('dashboardId', dashboardId.toString());

    attachments.forEach((file) => {
      formData.append('files', file.file, file.originalFileName);
    });

    return this.http.post<IGetManyResponse<number>>('widget-attachments/bulk/create', formData, {
      headers: new HttpHeaders({ [API_CACHE_MODE_HEADER]: EApiCacheMode.none }),
    });
  }

  public updateNote(widgetId: number, note: string): Observable<IBaseResponse<IWidget>> {
    return this.http.patch<IBaseResponse<IWidget>>(`widgets/${widgetId}/note`, { note });
  }

  public update(editDashboard: IEditDashboard): Observable<IBaseResponse<unknown>> {
    return this.http.patch<IBaseResponse<IDashboardWithWidgets>>(`dashboards/${editDashboard.id}`, editDashboard.dto);
  }
}
